import isEqual from 'lodash/isEqual';
import { Text } from 'slate';
import type { DeepPartial } from 'ts-essentials';

import { iterateInlineTextNodes } from './iterateInlineTextNodes';
import type { AlineText, ParagraphChildElement, ParagraphElement } from '../../slate-api';
import { isLinkElement } from '../../slate-api';
import type { CastInlineContent, CastTextStyle } from '../types';

export function extractCastFromParagraphText(paragraph: ParagraphElement): CastInlineContent[] {
  return extractInlineContent(paragraph.children);
}

function extractInlineContent(nodes: ParagraphChildElement[]): CastInlineContent[] {
  const segments: CastInlineContent[] = [];

  for (const node of nodes) {
    if (Text.isText(node)) {
      pushTextNode(segments, node);
      continue;
    }

    if (isLinkElement(node)) {
      const children = extractInlineContent(node.children);
      if (children.length > 0) segments.push({ children, href: node.href, type: 'link' });
      continue;
    }

    /**
     * Any other inline element (fields, breaks, tabs, markers, …) holds only
     * text children today, so we flatten its text into the surrounding run.
     * iterateInlineTextNodes carries the compile-time guard that trips if that
     * ever stops being true.
     */
    for (const textNode of iterateInlineTextNodes([node])) {
      pushTextNode(segments, textNode);
    }
  }

  return segments;
}

function pushTextNode(segments: CastInlineContent[], node: DeepPartial<AlineText>): void {
  const value = node.text ?? '';
  if (value === '') return;

  const style = extractStyleFromTextNode(node);
  const insertAuthorId = findFirstAuthorByType(node, 'insertNode');
  const deleteAuthorId = findFirstAuthorByType(node, 'deleteNode');
  const commentIds = extractCommentIds(node);

  const last = segments[segments.length - 1];
  if (
    last &&
    last.type === 'text' &&
    isEqual(last.style, style) &&
    last.insertAuthorId === insertAuthorId &&
    last.deleteAuthorId === deleteAuthorId &&
    isEqual(last.commentIds, commentIds)
  ) {
    last.value += value;
    return;
  }

  const segment: CastInlineContent = { type: 'text', value };
  if (style) segment.style = style;
  if (insertAuthorId !== undefined) segment.insertAuthorId = insertAuthorId;
  if (deleteAuthorId !== undefined) segment.deleteAuthorId = deleteAuthorId;
  if (commentIds !== undefined) segment.commentIds = commentIds;
  segments.push(segment);
}

/** Sorted and deduped, so runs that differ only in id order still merge. */
function extractCommentIds(node: DeepPartial<AlineText>): string[] | undefined {
  const ids = (node.comments ?? []).filter((id): id is string => typeof id === 'string');
  if (ids.length === 0) return undefined;
  return [...new Set(ids)].sort();
}

/**
 * Mirrors extract-paragraphs/docx/extractFromParagraphText: fontFamily is
 * intentionally not recorded because every AlineText carries a fontType
 * default, which would make the field meaningless noise on the prompt.
 */
function extractStyleFromTextNode(node: DeepPartial<AlineText>): CastTextStyle | undefined {
  const style: CastTextStyle = {};
  if (node.bold?.active) style.bold = true;
  if (node.italic?.active) style.italic = true;
  if (node.underline?.active) style.underline = true;
  if (node.strikethrough?.active) style.strikethrough = true;
  if (node.fontSize?.size != null) style.fontSize = node.fontSize.size;
  return Object.keys(style).length > 0 ? style : undefined;
}

function findFirstAuthorByType(
  node: DeepPartial<AlineText>,
  editType: 'insertNode' | 'deleteNode'
): string | undefined {
  for (const edit of node.suggestedEdits ?? []) {
    if (edit?.type === editType && typeof edit.authorId === 'string') return edit.authorId;
  }
  return undefined;
}
